/**
 * Shared geometry for the bloodwork PDF, all of it in CAPTURE PIXELS — the
 * pixels html2canvas renders and html2pdf slices. One page is
 * PDF_PAGE_BREAK_GRID_PX (1073) of them at PDF_CAPTURE_WIDTH_PX (801) wide; see
 * the derivation in bloodwork-analysis-pdf.tsx.
 *
 * ONE UNIT, everywhere. The Insights panels measure themselves off the rendered
 * card, the bar-graph pair below was measured off a real export, and the page
 * capacities are read off that same page geometry. There was briefly a
 * conversion between the panels' pixels and the packer's older hand-tuned
 * scale; once the bar-graph pair was re-measured in capture pixels the two
 * scales became one, and the conversion silently became a 22% under-count.
 * Nothing here converts any more, which is the point — a second unit is what
 * made the arithmetic wrong twice.
 */

/**
 * A section's bar-graph card, in the packer's units: `BASE + markers * ROW`.
 *
 * MEASURED off the 2026-08-31 export, converted to capture pixels through the
 * page geometry the container derives (a page is PDF_PAGE_BREAK_GRID_PX tall at
 * PDF_CAPTURE_WIDTH_PX wide, so the conversion is arithmetic, not a guess):
 *
 *     markers   rendered   old estimate (180 + 40n)
 *        5         368            380     over
 *        7         484            460     UNDER
 *        9         561            540     UNDER
 *       14         861            740     UNDER by 121
 *
 * The old pair was calibrated on short sections and drifted short as they grew,
 * so the packer thought a fourteen-marker section ended 121px above where it
 * really did and placed what followed against a stale page position. The pair
 * below fits all four points and rounds long at every one of them.
 *
 * Shared because two places ask the same question: the packer in
 * bloodwork-analysis-pdf.tsx budgets pages with it, and pdf-section.tsx uses it
 * to decide whether a card fits under an Insights panel. They were separate
 * copies and had already drifted apart.
 */
export const BAR_GRAPH_BASE_HEIGHT = 100;
export const BAR_GRAPH_ROW_HEIGHT = 56;
/** A text result prints as one row of labeled columns rather than a chart. */
export const BAR_GRAPH_TEXT_ROW_HEIGHT = 33;
/**
 * ...and the block of them is ruled off above and below, which costs its 19px
 * ONCE however many rows there are. Both numbers are measured, not guessed.
 */
export const BAR_GRAPH_TEXT_BLOCK_HEIGHT = 19;

/**
 * Approx characters that fit on one visual row of a note card's body at the
 * body font size. Both note cards render through `renderNoteBody` into a
 * `pre-wrap` block of the same width, so they wrap at the same place.
 */
export const NOTE_CHARS_PER_ROW = 90;
/** A `## ` line renders as an h6, not as body text. */
export const NOTE_HEADING_HEIGHT = 44;
/**
 * A blank line separating two blocks costs the block margin rather than a whole
 * row — `renderNoteBody` trims the blank lines off each block's edges.
 */
export const NOTE_BLANK_LINE_HEIGHT = 8;

/**
 * Rough height (capture px) of a practitioner note card: its fixed overhead
 * plus its text, counted in WRAPPED ROWS.
 *
 * The wrap is the whole point. A note typed as one long paragraph with no
 * Returns is a single `\n`-delimited line but renders as dozens of rows, so
 * counting lines under-estimates it by an order of magnitude — the container
 * then reads the card as fitting in the space left on the last section's page
 * and it spills. Errs high; over-estimating only pushes the card to a fresh
 * page, which is the safe direction.
 *
 * Shared by the two trailing note cards (Section Notes and Insights), which had
 * separate copies of this arithmetic and had drifted: Section Notes counted
 * wrapped rows and Insights counted lines. `cardOverhead` and `lineHeight` stay
 * per-card because each card's chrome and body type differ.
 */
export function estimateNoteCardHeight(
  text: string,
  { cardOverhead, lineHeight }: { cardOverhead: number; lineHeight: number },
): number {
  if (!(text || '').trim()) return 0;
  let h = cardOverhead;
  text.split('\n').forEach(line => {
    const t = line.trim();
    if (t.startsWith('## ')) h += NOTE_HEADING_HEIGHT;
    else if (t === '') h += NOTE_BLANK_LINE_HEIGHT;
    else h += Math.max(1, Math.ceil(line.length / NOTE_CHARS_PER_ROW)) * lineHeight;
  });
  return h;
}
